import { useRouter } from "next/router";
import { ParsedUrlQuery } from "querystring";
import { useCallback, useEffect, useMemo, useState } from "react";

import { ServiceReserveStatus, ServiceViewType } from "@/apis/clients";
import { ApiValidationError } from "@/apis/errors";
import EditorFrame from "@/components/editors/EditorFrame";
import CtaButton1Content from "@/components/editors/template-blocks/cta-button/cta-button-items/CtaButton1Content";
import CtaButton2Content from "@/components/editors/template-blocks/cta-button/cta-button-items/CtaButton2Content";
import CtaButton3Content from "@/components/editors/template-blocks/cta-button/cta-button-items/CtaButton3Content";
import ServiceButtonContent from "@/components/editors/template-blocks/cta-button/cta-button-items/ServiceButtonContent";
import { CommonModal } from "@/components/molecules/modals/CommonModal";
import { useSubmitForm } from "@/hooks/useSubmitForm";
import { useLoading } from "@/states/loading/operations";
import { CtaButtonTypeTemplate } from "@/states/page-editor/template-parts/cta-button";
import {
  useHandleSubmitForm,
  useSetIsSummited,
  useValidateReservation,
} from "@/states/service-form/operations";
import {
  useCannotOrderReasons,
  useFormData,
  useIsAgreePolicy,
  useResetServiceFormUiState,
  useServiceFormUiState,
  useServiceInfo,
} from "@/states/service-form/selectors";
import { scrollToElement } from "@/utils/common";
import CtaButton4Content from "./cta-button-items/CtaButton4Content";
import CtaButton5Content from "./cta-button-items/CtaButton5Content";
import WarningIC from "/public/icon/warning_ic.svg";


interface Props {
  part: CtaButtonTypeTemplate;
  formPath?: string;
  confirmPath?: string;
}
interface QueryParams {
  lpUuid?: string;
  simpleUuid?: string;
  iframeUuid?: string;
}
export default function CtaButtonTemplate({
  part,
  formPath,
  confirmPath,
}: Props) {
  const withLoading = useLoading();
  const service = useServiceInfo();
  const formData = useFormData();
  const serviceFormUiState = useServiceFormUiState();
  const { canSubmitOnLp } = useSubmitForm();
  const cannotOrderReasons = useCannotOrderReasons();
  const isAgreePolicy = useIsAgreePolicy();
  const setIsSummited = useSetIsSummited();
  const resetServiceFormUiState = useResetServiceFormUiState();

  const router = useRouter();

  const [submitErros, setSubmitErros] = useState<string[]>([]);
  const [canNextPage, setNextPage] = useState(
    formPath && canSubmitOnLp ? true : canSubmitOnLp,
  );
  const [nextLink, setNextLink] = useState(
    service?.type === "calendar" ? "" : formPath ? formPath : confirmPath,
  );

  // const isShowTerm = service?.viewType === ServiceViewType.FORM;
  let isShowTerm = false;
  if (service?.type && ["survey", "document-request", "email-newsletter"].includes(service?.type)) isShowTerm = true;
  const isEventSeminar = service?.type === "event-seminar";

  useEffect(() => {
    const canNext = canSubmitOnLp && (!isShowTerm || isAgreePolicy);
    setNextPage(formPath && canNext ? true : canNext);
    setNextLink(
      service?.type === "calendar" ? "" : formPath ? formPath : confirmPath,
    );
  }, [canSubmitOnLp, confirmPath, formPath, isAgreePolicy, isShowTerm, service?.type]);

  const queryParams: QueryParams | undefined = useMemo(() => {
    if (!router.isReady) {
      return undefined;
    }
    const getLpId = (query: ParsedUrlQuery) => {
      const { uuid } = query;
      if (uuid === undefined || typeof uuid !== "string") {
        return undefined;
      }
      return uuid;
    };

    const getSimpleId = (query: ParsedUrlQuery) => {
      const { simpleId } = query;
      if (simpleId === undefined || typeof simpleId !== "string") {
        return undefined;
      }
      return simpleId;
    };

    const getIframeId = (query: ParsedUrlQuery) => {
      const { iframeId } = query;
      if (iframeId === undefined || typeof iframeId !== "string") {
        return undefined;
      }
      return iframeId;
    };

    return {
      lpUuid: getLpId(router.query),
      simpleUuid: getSimpleId(router.query),
      iframeUuid: getIframeId(router.query),
    };
  }, [router.isReady, router.query]);
  const isPaymentFormFlg = useCallback(() => {
    if (!service) return false;

    if (service.type === "calendar") {
      if (serviceFormUiState.status === "Loaded") {
        if (!serviceFormUiState.inputs.service) return false;
        const serviceMenu = service.menus.filter(
          (menu) => menu.id === serviceFormUiState.inputs.service.serviceMenuId,
        );
        if (serviceMenu.length > 0 && serviceMenu[0].price > 0) return true;
      }
      return false;
    }

    if (service.type === "event-seminar" || service.type === "form") {
      let playFlagResult = service.details.map((detail) => {
        return detail.price && detail.price > 0;
      });
      if (service.reserveStatus === ServiceReserveStatus.ALL) {
        playFlagResult = service.details.map((detail) => {
          return detail.price && detail.price > 0;
        });
      }

      return playFlagResult.includes(true);
    }

    return false;
  }, [service]);
  const formUuid = useMemo(() => {
    const { uuid: formUuid } = router.query;
    if (formUuid === undefined || typeof formUuid !== "string") {
      return undefined;
    }
    return formUuid;
  }, [router.query]);
  useEffect(() => {
    if (
      (service?.type === "event-seminar" || service?.type === "form") &&
      isPaymentFormFlg()
    ) {
      let query = "";
      let path = "";
      if (queryParams?.lpUuid) {
        query = `lpId=${queryParams.lpUuid}&from=${service?.viewType}`;
      } else if (queryParams?.simpleUuid) {
        query = `simpleId=${queryParams.simpleUuid}&from=${service?.viewType}`;
      } else if (queryParams?.iframeUuid) {
        query = `iframeId=${queryParams.iframeUuid}&from=${service?.viewType}`;
      } else {
        query = `from=${service?.viewType}`;
      }
      if (service?.viewType === ServiceViewType.FORM) {
        path = `/form/${formUuid || ""}/payment?${query}`;
        setNextLink(path);
        return;
      }
    } else if (service?.type === "calendar") {
      // Calendar service CTA button on page scrolls to menu instead of navigating
      setNextLink("");
    } else if (
      service?.type === "survey" ||
      service?.type === "document-request" ||
      service?.type === "email-newsletter"
    ) {
      setNextLink(formPath || "");
    }
  }, [
    formUuid,
    isPaymentFormFlg,
    queryParams?.lpUuid,
    queryParams?.simpleUuid,
    queryParams?.iframeUuid,
    service,
    canSubmitOnLp,
    canNextPage,
    confirmPath,
    formPath,
    router,
  ]);

  const disabled = useMemo(() => {
    // A CTA with its own link is an ordinary link, not the application button, so the
    // form's loading state must not disable it (redmine #1066).
    if (part.buttonContent?.link?.url) return false;

    if (serviceFormUiState?.status !== "Loaded") return true;
    if (isShowTerm) {
      if (part.type.includes("cta_button")) return false;
      if (!isAgreePolicy) return !isAgreePolicy;
    }
    return cannotOrderReasons.length > 0;
  }, [
    cannotOrderReasons.length,
    isAgreePolicy,
    isShowTerm,
    part.type,
    serviceFormUiState?.status,
    part.buttonContent?.link?.url,
  ]);

  const goToErrorText = useCallback(() => {
    window.dispatchEvent(new Event("button.handleFormSubmit"));
    if (!canNextPage) {
      const errorElements = document.getElementsByClassName("form-error-text");
      if (errorElements && errorElements[0]) {
        scrollToElement(errorElements[0], "center");
      } else if (isShowTerm && !isAgreePolicy) {
        const termElements = document.getElementById("term-policy");
        scrollToElement(termElements, "center");
      }
      return true;
    }
    return false;
  }, [canNextPage, isAgreePolicy, isShowTerm]);

  const handleGoToForm = () => {

    // Service Calendar
    // Scroll into menu calendar
    const menuCalendarElement = document.getElementById(
      "service-menu-calendar-template",
    );
    if (menuCalendarElement) {
      scrollToElement(menuCalendarElement, "start");
      return;
    }

    // Other Service
    // Scroll into require menu
    const requireMenuElement = document.getElementById("service-menu-template");
    if (
      requireMenuElement &&
      service?.reserveStatus !== ServiceReserveStatus.ALL &&
      service?.details &&
      service?.details.length > 1
    ) {
      scrollToElement(requireMenuElement, "start");
      return;
    }
    // Scroll into form
    const formElement = document.getElementById("service-form-template");
    if (formElement) {
      scrollToElement(formElement, "start");
      return;
    }
    // Scroll into menu
    const menuElement = document.getElementById("service-menu-template");
    if (menuElement) {
      scrollToElement(menuElement, "start");
      return;
    }
  };
  const handleSubmitService = () => {
    setIsSummited({ ...formData, isSubmit: true });
    setTimeout(() => {
      if (!goToErrorText()) {
        // Validate for event-seminar before submitting
        if (service?.type === "event-seminar" && serviceFormUiState.status === "Loaded") {
          void validateReservation().then((validationResult) => {
            if (validationResult.isValid) {
              void router.push(nextLink || "");
            }
          });
        } else {
          void onClickSubmit();
        }
      }
    }, 100);
  };
  const handleSubmitForm = useHandleSubmitForm();
  const validateReservation = useValidateReservation();
  const onClickSubmit = useCallback(async () => {
    await withLoading(async () => {
      try {
        const result = await handleSubmitForm(formUuid);
        let thanksUrl = `/thanks/${result.thanksUuid}?order_uuid=${result.orderUuid
          }&lp_uiid=${queryParams?.lpUuid || ""}`;

        if (
          !(service?.type === "survey" || service?.type === "email-newsletter")
        ) {
          thanksUrl += `&reservation_code=${result.id}`;
        }
        await router.push(thanksUrl);
        resetServiceFormUiState();
      } catch (e) {
        if (e instanceof ApiValidationError) {
          for (const key in e.errors) {
            const err = e.errors[key];
            setSubmitErros(err);
          }
          return;
        }
        throw e;
      }
    });
  }, [formUuid, router, withLoading, handleSubmitForm]);

  // only check with event-seminar
  const isErrorMaxTicket = serviceFormUiState.status === "Loaded" && serviceFormUiState?.errors?.service?.validationError;

  return (
    <div
      className={`mx-[-16px] px-[16px] md:mx-[calc((-100vw+920px)/2)] md:px-[calc((100vw-920px)/2)]`}
      style={part.bgCssProperties}
    >
      <EditorFrame noBorder isNoPadding={part.type.includes("menu")}>
        {cannotOrderReasons.length > 0 &&
          service?.viewType === ServiceViewType.BUTTON && (
            <div className="">
              {cannotOrderReasons.map((reason, index) => (
                <p key={index} className="text-xl text-center text-accent">
                  {reason}
                </p>
              ))}
            </div>
          )}
        {isErrorMaxTicket &&
          <div className="flex md:flex-row items-start  bg-[#FFFBDB] py-2 px-4 border-[1px] border-[#FFE58F] rounded-lg text-xs max-w-[620px] mx-auto">
            <WarningIC className="mr-2 mt-[2px] h-4 w-5" />
            {serviceFormUiState?.errors?.service?.validationError}
          </div>}
        <CtaButtonContent
          part={part}
          formPath={(canNextPage && !isEventSeminar) ? nextLink : ""}
          onSubmit={handleSubmitService}
          onGoToForm={handleGoToForm}
          disabled={disabled}
          isShowTerm={isShowTerm}
        />
      </EditorFrame>
      <CommonModal
        isOpen={submitErros.length > 0}
        close={() => setSubmitErros([])}
        submit={() => setSubmitErros([])}
        title={"申し込みできませんでした"}
        btnText="閉じる"
      >
        {submitErros?.map((message, index) => (
          <div key={`error-message-${index}`}>{message}</div>
        ))}
      </CommonModal>
    </div>
  );
}

interface CtaButtonContentProps {
  part: CtaButtonTypeTemplate;
  formPath: string | undefined;
  onSubmit?: () => void;
  onGoToForm?: () => void;
  disabled?: boolean;
  isShowTerm?: boolean;
}

function CtaButtonContent({
  part,
  formPath,
  onSubmit,
  onGoToForm,
  disabled,
  isShowTerm,
}: CtaButtonContentProps) {
  const canButtonSubmit = true;
  switch (part.type) {
    case "cta_button.1":
      return (
        <CtaButton1Content
          part={part}
          formPath={canButtonSubmit ? formPath : undefined}
          onClick={onGoToForm}
          disabled={disabled}
        />
      );
    case "cta_button.2":
      return (
        <CtaButton2Content
          part={part}
          formPath={canButtonSubmit ? formPath : undefined}
          onClick={onGoToForm}
          disabled={disabled}
        />
      );
    case "cta_button.3":
      return (
        <CtaButton3Content
          part={part}
          formPath={canButtonSubmit ? formPath : undefined}
          onClick={onGoToForm}
          disabled={disabled}
        />
      );
    case "cta_button.4":
      return (
        <CtaButton4Content
          part={part}
          formPath={canButtonSubmit ? formPath : undefined}
          onClick={onGoToForm}
        />
      );
    case "cta_button.5":
      return (
        <CtaButton5Content
          part={part}
          formPath={canButtonSubmit ? formPath : undefined}
          onClick={onGoToForm}
        />
      );

    case "menu.form":
    case "menu.eventSeminar":
    case "menu.subscription":
    case "menu.other":
      return (
        <ServiceButtonContent
          part={part}
          formPath={formPath}
          onClick={onSubmit}
          disabled={disabled}
          isShowTerm={isShowTerm}
        />
      );
    default:
      return <></>;
  }
}
